import React, { useState, useEffect } from 'react';
import { Sparkles, TrendingUp, Ticket, CreditCard, Users, Lightbulb, ChevronDown, ChevronUp, Save, Check, Loader2, Lock } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import useFeatureGating from '../../hooks/useFeatureGating';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ===================== helpers ===================== */

function formatCurrency(n) {
  const num = Number(n || 0);
  return num.toLocaleString(undefined, { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });
}

function attendanceBand(pct) {
  if (pct >= 75) return { label: 'Strong attendance',   tone: 'text-emerald-400' };
  if (pct >= 50) return { label: 'Moderate attendance', tone: 'text-[#D4AF37]' };
  return             { label: 'Low attendance',      tone: 'text-[#C44545]' };
}

function capitalize(s) {
  if (!s) return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/* ===================== primitives ===================== */

function InsightCard({ icon: Icon, title, children, testId }) {
  return (
    <div
      className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm p-4 flex flex-col gap-3 min-w-0"
      data-testid={testId}
    >
      <div className="flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />}
        <h3 className="text-[11px] uppercase tracking-[0.18em] text-[#777] font-semibold">{title}</h3>
      </div>
      <div className="space-y-2 text-sm">{children}</div>
    </div>
  );
}

function StatRow({ label, value, accent, testId }) {
  return (
    <div className="flex items-baseline justify-between gap-3 min-w-0" data-testid={testId}>
      <span className="text-[#777] text-xs truncate">{label}</span>
      <span className="font-semibold tabular-nums" style={{ color: accent || '#EAE7E1' }}>{value}</span>
    </div>
  );
}

function Recommendation({ text, testId }) {
  return (
    <div className="text-[11px] leading-relaxed text-[#D4AF37] bg-[#D4AF37]/5 border border-[#D4AF37]/15 rounded-sm px-2.5 py-2" data-testid={testId}>
      {text}
    </div>
  );
}

/* ===================== component ===================== */

export default function PostEventInsights({ report, filters }) {
  const [expanded, setExpanded] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const { canUse } = useFeatureGating();
  const canUseInsights = canUse('post_event_insights');
  const canUseSnapshot = canUse('insight_snapshots');

  const selectedCount = (filters?.event_ids || []).length;
  const selectedEventIdForEffect = selectedCount === 1 ? filters.event_ids[0] : null;

  // Reset "saved" state when the selected event changes (so the button re-enables).
  // MUST be declared before any early return to satisfy Rules of Hooks.
  useEffect(() => { setSaved(false); }, [selectedEventIdForEffect]);

  // Multi-select → hint only
  if (selectedCount >= 2) {
    return (
      <section className="mb-6" data-testid="post-event-insights-multi">
        <h2 className="text-sm font-semibold text-[#EAE7E1] mb-3 tracking-wide uppercase">Post Event Insights</h2>
        <div className="bg-[#0A0A0A] border border-dashed border-[#1A1A1A] rounded-sm p-5 text-center">
          <p className="text-xs text-[#777]" data-testid="post-event-insights-select-one-note">
            Select one event to view Post Event Insights.
          </p>
        </div>
      </section>
    );
  }

  // None selected → hide entirely
  if (selectedCount === 0) return null;

  // Soft preview gate — user selected exactly one event but does not have
  // the feature. Render a placeholder card with blurred metrics instead
  // of hiding. No layout shift; same section height-ish.
  if (!canUseInsights) {
    const selectedEventId = filters.event_ids[0];
    const eventRow = (report?.events || []).find(e => e.event_id === selectedEventId);
    const eventName = eventRow?.event_name || 'This Event';
    return (
      <section className="mb-6" data-testid="post-event-insights-preview">
        <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#D4AF37]" />
              <h2 className="text-sm font-semibold text-[#EAE7E1] tracking-wide uppercase">Post Event Insights</h2>
              <span className="text-[9px] uppercase tracking-widest text-[#D4AF37]/80 border border-[#D4AF37]/25 rounded-sm px-1.5 py-0.5" data-testid="post-event-insights-pro-label">Pro feature</span>
            </div>
            <p className="text-xs text-[#777] mt-1 max-w-xl">
              Understand how this event performed and what to improve next time.
            </p>
          </div>
          <div className="px-3 py-1 rounded-sm bg-[#0A0A0A] border border-[#D4AF37]/30 text-[11px] text-[#D4AF37] max-w-[220px] truncate" title={eventName} data-testid="post-event-insights-event-pill">
            Event: <span className="font-semibold">{eventName}</span>
          </div>
        </div>
        <div className="relative bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm p-6 overflow-hidden" data-testid="post-event-insights-preview-card">
          {/* Blurred placeholder metrics */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 blur-sm select-none pointer-events-none" aria-hidden="true">
            {['Passes','Attendees','Check-In','Gross','Comp'].map(l => (
              <div key={l}>
                <div className="text-[10px] uppercase tracking-wider text-[#777]">{l}</div>
                <div className="text-base font-semibold text-[#EAE7E1]">•••</div>
              </div>
            ))}
          </div>
          <div className="absolute inset-0 flex items-center justify-center bg-[#050505]/70">
            <div className="text-center px-4">
              <Lock className="w-5 h-5 text-[#D4AF37] mx-auto mb-2" />
              <p className="text-sm font-semibold text-[#EAE7E1]">Upgrade to Pro to unlock event insights</p>
              <p className="text-xs text-[#B9B1A6] mt-1">See top tier, payment mix, returning guests, and tailored recommendations.</p>
              <Link to="/guide#plans-access" className="inline-block mt-3 text-[11px] text-[#D4AF37] hover:underline" data-testid="post-event-insights-learn-more">
                Learn more
              </Link>
            </div>
          </div>
        </div>
      </section>
    );
  }

  const selectedEventId = filters.event_ids[0];
  const eventRow = (report?.events || []).find(e => e.event_id === selectedEventId);
  const eventName = eventRow?.event_name || 'Unknown Event';

  // Save Snapshot handler — POST /api/events/{id}/snapshot-insights.
  // Per spec: no modal, no confirmation, one-click save.
  const handleSaveSnapshot = async () => {
    if (!selectedEventId || saving || saved) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('session_token');
      await axios.post(
        `${API}/events/${selectedEventId}/snapshot-insights`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSaved(true);
      toast.success('Snapshot saved');
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Could not save snapshot';
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  // ---- Derive per-card values from the already-filtered report ----

  // Event Performance (uses report-level KPIs since only one event is selected; they represent this event)
  const kpis = report?.kpis || {};
  const totalPasses = kpis.total_passes || 0;
  const totalAttendees = kpis.total_attendees || 0;
  const checkedInPct = kpis.checked_in_percent || 0;
  const reportedGross = report?.reported_gross_total || 0;
  const cvp = report?.comp_vs_paid || { comp_count: 0, paid_count: 0, comp_percent: 0 };
  const compPct = cvp.comp_percent || 0;
  const band = attendanceBand(checkedInPct);

  // Ticket Performance
  const tiers = report?.ticket_type_breakdown || [];
  const topTier = tiers[0] || null;
  const lowestTier = tiers.length >= 2 ? tiers[tiers.length - 1] : null;

  // Payment Insights
  const pmMix = report?.payment_method_mix || [];
  const topPm = pmMix[0] || null;

  // Guest Insights
  const guests = report?.guest_attendance_summary || [];
  const returning = guests.filter(g => (g.events_attended || 0) >= 2);
  const newGuests = guests.filter(g => (g.events_attended || 0) === 1);
  const returningPct = guests.length ? Math.round((returning.length / guests.length) * 1000) / 10 : 0;
  const topAttendee = guests[0] || null; // already sorted by total_passes desc by backend

  // Recommendations
  const recs = [];
  if (compPct >= 40) {
    recs.push('High comp usage detected — review comp strategy for future events.');
  }
  if (checkedInPct < 50 && totalPasses > 0) {
    recs.push('Low check-in rate — consider stronger guest confirmations.');
  }
  if (topTier && (topTier.percent_of_total || 0) >= 40) {
    recs.push('Top tier is driving demand — consider expanding or repricing it.');
  }
  if (topPm && (topPm.percent || 0) >= 70) {
    const methodLabel = capitalize(topPm.method || '');
    recs.push(`Most paid tickets used ${methodLabel} — consider making that route easiest to find.`);
  }

  return (
    <section className="mb-6" data-testid="post-event-insights">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#D4AF37]" />
            <h2 className="text-sm font-semibold text-[#EAE7E1] tracking-wide uppercase">Post Event Insights</h2>
          </div>
          <p className="text-xs text-[#777] mt-1 max-w-xl">
            Understand how this event performed and what to improve next time.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {canUseSnapshot ? (
            <button
              type="button"
              onClick={handleSaveSnapshot}
              disabled={saving || saved}
              className={`inline-flex items-center gap-1.5 text-[11px] rounded-sm px-2.5 py-1 transition-colors border ${saved ? 'border-emerald-500/40 text-emerald-400 bg-emerald-500/5 cursor-default' : 'border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10'}`}
              data-testid="post-event-insights-save-snapshot"
            >
              {saving ? (<><Loader2 className="w-3 h-3 animate-spin" /> Saving…</>) : saved ? (<><Check className="w-3 h-3" /> Saved</>) : (<><Save className="w-3 h-3" /> Save Snapshot</>)}
            </button>
          ) : (
            <button
              type="button"
              disabled
              title="Pro feature"
              className="inline-flex items-center gap-1.5 text-[11px] rounded-sm px-2.5 py-1 border border-[#1A1A1A] text-[#555] cursor-not-allowed"
              data-testid="post-event-insights-save-snapshot-locked"
            >
              <Lock className="w-3 h-3" /> Save Snapshot
              <span className="ml-1 text-[9px] uppercase tracking-widest text-[#D4AF37]/80">Pro</span>
            </button>
          )}
          <div
            className="px-3 py-1 rounded-sm bg-[#0A0A0A] border border-[#D4AF37]/30 text-[11px] text-[#D4AF37] max-w-[220px] truncate"
            title={eventName}
            data-testid="post-event-insights-event-pill"
          >
            Event: <span className="font-semibold">{eventName}</span>
          </div>
          <button
            type="button"
            onClick={() => setExpanded(v => !v)}
            className="inline-flex items-center gap-1 text-[11px] text-[#AFAFAF] hover:text-[#EAE7E1] border border-[#1A1A1A] rounded-sm px-2 py-1 transition-colors"
            data-testid="post-event-insights-toggle"
          >
            {expanded ? (<><ChevronUp className="w-3 h-3" /> Hide Insights</>) : (<><ChevronDown className="w-3 h-3" /> Show Insights</>)}
          </button>
        </div>
      </div>

      {!expanded && (
        <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm p-3 text-xs text-[#777]" data-testid="post-event-insights-collapsed">
          Insights hidden. Click <b className="text-[#D4AF37]">Show Insights</b> to view the debrief for <b className="text-[#EAE7E1]">{eventName}</b>.
        </div>
      )}

      {expanded && (
        <>
          {/* Row 1 — 3 cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="post-event-insights-row1">
            {/* Card 1 — Event Performance */}
            <InsightCard icon={TrendingUp} title="Event Performance" testId="insights-event-performance">
              <StatRow label="Total Passes"    value={totalPasses}                       testId="insights-total-passes" />
              <StatRow label="Total Attendees" value={totalAttendees}                    testId="insights-total-attendees" />
              <StatRow label="Checked-In %"    value={`${checkedInPct}%`}                accent="#C6A66B" testId="insights-checkedin-pct" />
              <StatRow label="Reported Gross"  value={formatCurrency(reportedGross)}     accent="#C6A66B" testId="insights-reported-gross" />
              <StatRow label="Comp Rate"       value={`${compPct}%`}                     testId="insights-comp-rate" />
              {totalPasses > 0 && (
                <div className={`text-[11px] mt-1 ${band.tone}`} data-testid="insights-attendance-band">
                  {band.label}
                </div>
              )}
            </InsightCard>

            {/* Card 2 — Ticket Performance */}
            <InsightCard icon={Ticket} title="Ticket Performance" testId="insights-ticket-performance">
              {tiers.length === 0 ? (
                <p className="text-xs text-[#555]" data-testid="insights-ticket-empty">No ticket data yet</p>
              ) : (
                <>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#555]">Top Tier</div>
                    <div className="text-sm text-[#EAE7E1] font-semibold truncate" title={topTier?.tier_name} data-testid="insights-top-tier">
                      {topTier?.tier_name}
                    </div>
                    <div className="text-xs text-[#D4AF37] tabular-nums" data-testid="insights-top-tier-pct">
                      {topTier?.percent_of_total}% of total · {topTier?.tickets_sold} passes
                    </div>
                  </div>
                  {lowestTier && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-[#555] mt-2">Lowest Tier</div>
                      <div className="text-sm text-[#B9B1A6] truncate" title={lowestTier?.tier_name} data-testid="insights-lowest-tier">
                        {lowestTier?.tier_name}
                      </div>
                      <div className="text-xs text-[#777] tabular-nums" data-testid="insights-lowest-tier-pct">
                        {lowestTier?.percent_of_total}% of total · {lowestTier?.tickets_sold} passes
                      </div>
                    </div>
                  )}
                  {topTier && (topTier.percent_of_total || 0) >= 40 && (
                    <Recommendation testId="insights-rec-top-tier-demand" text="Top tier is driving demand" />
                  )}
                  {lowestTier && (lowestTier.percent_of_total || 0) <= 10 && (
                    <Recommendation testId="insights-rec-lowest-tier-reposition" text="Lowest tier may need repositioning" />
                  )}
                </>
              )}
            </InsightCard>

            {/* Card 3 — Payment Insights */}
            <InsightCard icon={CreditCard} title="Payment Insights" testId="insights-payment">
              {!topPm ? (
                <p className="text-xs text-[#555]" data-testid="insights-payment-empty">No payment data yet</p>
              ) : (
                <>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#555]">Top Payment Method</div>
                    <div className="text-sm text-[#EAE7E1] font-semibold capitalize" data-testid="insights-top-payment">
                      {topPm.method}
                    </div>
                    <div className="text-xs text-[#D4AF37] tabular-nums" data-testid="insights-top-payment-pct">
                      {topPm.percent}% of paid · {topPm.count} passes
                    </div>
                  </div>
                  <StatRow label="Methods Used" value={pmMix.length} testId="insights-methods-count" />
                  {(topPm.percent || 0) >= 70 && (
                    <Recommendation testId="insights-rec-payment-preferred" text="Most guests prefer this payment route" />
                  )}
                </>
              )}
            </InsightCard>
          </div>

          {/* Row 2 — 2 cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3" data-testid="post-event-insights-row2">
            {/* Card 4 — Guest Insights */}
            <InsightCard icon={Users} title="Guest Insights" testId="insights-guest">
              {guests.length === 0 ? (
                <p className="text-xs text-[#555]" data-testid="insights-guest-empty">Guest history will appear as attendance grows</p>
              ) : (
                <>
                  <StatRow label="Returning Guests" value={returning.length}   testId="insights-returning-count" />
                  <StatRow label="New Guests"       value={newGuests.length}   testId="insights-new-count" />
                  <StatRow label="Returning Guest %" value={`${returningPct}%`} accent="#C6A66B" testId="insights-returning-pct" />
                  {topAttendee && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-[#555] mt-1">Top Attendee</div>
                      <div className="text-sm text-[#EAE7E1] truncate" title={topAttendee.guest_name || topAttendee.guest_email} data-testid="insights-top-attendee-name">
                        {topAttendee.guest_name || topAttendee.guest_email}
                      </div>
                      <div className="text-[11px] text-[#777] tabular-nums" data-testid="insights-top-attendee-stats">
                        {topAttendee.total_passes} passes · {topAttendee.events_attended} event{topAttendee.events_attended === 1 ? '' : 's'}
                      </div>
                    </div>
                  )}
                </>
              )}
            </InsightCard>

            {/* Card 5 — Recommendations */}
            <InsightCard icon={Lightbulb} title="Insights & Recommendations" testId="insights-recommendations">
              {recs.length === 0 ? (
                <p className="text-xs text-[#555]" data-testid="insights-recs-empty">Nothing to flag — this event is within healthy ranges.</p>
              ) : (
                <div className="space-y-2" data-testid="insights-recs-list">
                  {recs.map((text, i) => (
                    <Recommendation key={i} text={text} testId={`insights-rec-${i}`} />
                  ))}
                </div>
              )}
            </InsightCard>
          </div>
        </>
      )}
    </section>
  );
}
